import type { Row, RowSelectionState } from '@tanstack/react-table';
import { Box, Checkbox, Table, Tooltip } from '@timelyapp/react';
import classNames from 'classnames';
import type { ForwardedRef, MouseEvent, MutableRefObject } from 'react';
import React, { forwardRef, useCallback } from 'react';
import styles from './column.module.scss';
import type {
  DatatableCellRenderer,
  DatatableColumnGetter,
  DatatableHeaderRenderer,
} from './types';

interface PartialRowData {
  selectionTooltip?: string;
}

export const getSelectionColumn: DatatableColumnGetter<
  number,
  { lastSelectedRowIndex: MutableRefObject<null | number> },
  PartialRowData
> = (id: string, params) => ({
  id,
  header: SelectionHeader,
  enableResizing: false,
  enableSorting: false,
  size: 40,
  maxSize: 40,
  minSize: 40,
  meta: {
    numeric: false,
  },
  cell: props => <SelectionCell {...props} ref={params!.lastSelectedRowIndex} />,
});

export const SelectionCell: DatatableCellRenderer<
  number,
  { ref: MutableRefObject<null | number> },
  PartialRowData
> = forwardRef(({ row, table }, ref: ForwardedRef<null | number>) => {
  const handleCellClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (row.getCanSelect()) {
        if (typeof ref === 'object' && ref !== null) {
          const shiftKeyPressed = event.shiftKey;
          const anchorIndex = ref.current;
          if (anchorIndex !== null && row.index !== anchorIndex && shiftKeyPressed) {
            // range was selected
            const { rows } = table.getRowModel();
            const rowsToToggle = getRowRange(rows, anchorIndex, row.index);
            const isAnchorRowSelected = rows[anchorIndex].getIsSelected();

            table.setRowSelection((selection: RowSelectionState) => {
              const updatedSelection = { ...selection };
              rowsToToggle.forEach(row => {
                if (isAnchorRowSelected) {
                  updatedSelection[row.id] = true;
                } else if (updatedSelection[row.id]) {
                  delete updatedSelection[row.id];
                }
              });
              return updatedSelection;
            });
          } else {
            row.toggleSelected();
          }

          ref.current = row.index;
        }
      }
    },
    [ref, row, table]
  );

  const selectionTooltip = row.original.selectionTooltip || null;

  return (
    <Box.Row
      alignItems="center"
      justifyContent="center"
      className={classNames(styles.selection, { [styles.canSelect]: row.getCanSelect() })}
      onClick={handleCellClick}
    >
      <Tooltip tooltip={selectionTooltip} usePortal>
        <Checkbox
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
          }}
          data-testid="checkbox"
        />
      </Tooltip>
    </Box.Row>
  );
});
export const SelectionHeader: DatatableHeaderRenderer = ({ header, table }) => {
  const isPinned = header.column.getIsPinned();

  const handleCellClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      const isAllSelected = table.getIsAllRowsSelected();
      table.toggleAllRowsSelected(!isAllSelected);
    },
    [table]
  );

  return (
    <Table.Th
      key={header.id}
      onClick={handleCellClick}
      sortable={header.column.getCanSort()}
      sorted={!!header.column.getIsSorted()}
      pinnedLeft={isPinned === 'left'}
      pinnedRight={isPinned === 'right'}
      {...{
        colSpan: header.colSpan,
        style: {
          width: header.getSize(),
        },
      }}
    >
      <Box.Row
        alignItems="center"
        justifyContent="center"
        className={classNames(styles.selection, styles.canSelect)}
      >
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      </Box.Row>
    </Table.Th>
  );
};

function getRowRange<T>(rows: Array<Row<T>>, anchorIndex: number, currentIndex: number) {
  const rangeStart = anchorIndex > currentIndex ? currentIndex : anchorIndex;
  const rangeEnd = rangeStart === currentIndex ? anchorIndex : currentIndex;
  return rows.slice(rangeStart, rangeEnd + 1);
}
